'use client';

import { Portal } from '@ark-ui/react';
import { useState } from 'react';
import { LegacyDialog } from '../../../../../components/dialog';
import { IconButton } from '../../../../../components/icon-button';
import { Maximize02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Tooltip } from '../../../../../components/tooltip';
import type { SignalData } from '../types';
import { EnhancedEditSignalForm } from '../forms/EnhancedEditSignalForm';

const HITL_MODAL_Z_INDEX = 'toast';

type EnhancedEditSignalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (editedSignals: SignalData[]) => void;
  onApprove?: () => void;
  onReject?: (message?: string) => void;
  signals: SignalData[];
  actionName?: string;
};

export function EnhancedEditSignalModal({
  isOpen,
  onClose,
  onSubmit,
  onApprove,
  onReject,
  signals,
  actionName,
}: EnhancedEditSignalModalProps) {
  const t = useTranslations('designSystem.chat.editSignalModal');
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <LegacyDialog.Backdrop zIndex={HITL_MODAL_Z_INDEX} />
        <LegacyDialog.Positioner zIndex={HITL_MODAL_Z_INDEX}>
          <LegacyDialog.Content
            maxWidth={isFullscreen ? '95vw' : '900px'}
            width="90vw"
            height={isFullscreen ? '95vh' : '80vh'}
            maxHeight={isFullscreen ? '95vh' : '80vh'}
          >
            <EnhancedEditSignalForm
              onClose={onClose}
              onSubmit={onSubmit}
              onApprove={onApprove}
              onReject={onReject}
              signals={signals}
              actionName={actionName}
              allowSubmitWithoutChanges={Boolean(onApprove)}
              headerActions={
                <Tooltip
                  title={isFullscreen ? t('exitFullscreen') : t('fullscreen')}
                >
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    aria-label={t('toggleFullscreen')}
                  >
                    <Maximize02Icon size={14} />
                  </IconButton>
                </Tooltip>
              }
            />
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </Portal>
    </LegacyDialog.Root>
  );
}
